import { z } from 'zod';
import { ExternalRefSchema } from '../external-ref/external-ref.schema';
/**
 * Correspondence language of an account. Drives which language the
 * invoice document is issued in — Moco supports exactly German and English,
 * so this is deliberately narrower than the app's UI locales.
 */ export const BillingLanguageSchema = z.enum([
    'de',
    'en'
]);
/**
 * Woher die Stammdaten des Kontos stammen (LIC-26).
 *
 * `crm` ist der Normalfall: das CRM führt sie, wir lesen. `provider` heißt,
 * dass der Kunde seine Adresse auf der Zahlseite korrigiert hat — die wurde
 * besteuert, also steht sie im Konto, und sie weicht vom CRM ab, bis der
 * Service sie dort nachzieht. Ohne diesen Vermerk wäre nach einer solchen
 * Korrektur nicht erklärbar, warum zwei Systeme Verschiedenes sagen.
 */ export const MasterDataOriginSchema = z.enum([
    'crm',
    'provider'
]);
/**
 * Die Felder, die Konto und CRM gemeinsam führen (LIC-29).
 *
 * Als Liste, nicht als Aufzählung im Code: der Abgleich läuft über genau
 * diese Namen, und ein Feld, das dazukommt, soll an einer Stelle dazukommen.
 */ export const SHARED_MASTER_DATA_FIELDS = [
    'companyName',
    'billingAddress',
    'vatId',
    'billingEmail'
];
export const BillingAddressSchema = z.object({
    line1: z.string().trim().min(1),
    line2: z.string().trim().optional(),
    postalCode: z.string().trim().min(1),
    city: z.string().trim().min(1),
    /** ISO 3166-1 alpha-2, uppercase — what both Stripe and Moco expect. */ country: z.string().trim().toUpperCase().length(2, 'Country must be an ISO 3166-1 alpha-2 code')
});
/**
 * Der zuletzt mit dem CRM abgeglichene Stand je Feld — der Schatten.
 *
 * Er beantwortet die Frage, die ein blosser Vergleich nicht beantworten kann:
 * nicht „sind beide Seiten verschieden", sondern **wer hat geändert**. Weicht
 * das CRM vom Schatten ab, war es der Vertrieb; weicht das Konto ab, war es
 * der Kunde; weichen beide ab, ist es ein echter Konflikt — und nur der
 * braucht eine Regel (LIC-29).
 *
 * Gesetzt beim Lesen **und** beim Schreiben. Ohne das Zweite hielte der
 * nächste Lauf den eigenen Schreibvorgang für eine fremde Änderung und
 * schriebe ihn zurück.
 */ export const CrmShadowSchema = z.object({
    companyName: z.string().nullable().optional(),
    billingAddress: BillingAddressSchema.nullable().optional(),
    vatId: z.string().nullable().optional(),
    billingEmail: z.string().nullable().optional(),
    /**
   * Die Person, an die die Rechnung geht.
   *
   * Ein Feld, nicht Vor- und Nachname getrennt: ein Titel gehoert in
   * deutschen Anschreiben zum Namen, und getrennte Felder zwaengen den Leser
   * zu entscheiden, wohin das "Prof. Dr." gehoert. Der Anbieter bekommt es
   * als Vornamen, weil das die einzige Zeile ist, die er ungeteilt ausgibt.
   *
   * Kein CRM-Feld: die uebrigen Stammdaten fuehrt HubSpot als
   * Firmeneigenschaft (LIC-26), eine Person waere dort ein Kontakt mit
   * Beziehung. Solange niemand das braucht, ist hier ein Name ein Name.
   */ billingContactName: z.string().nullable().optional(),
    /**
   * Ob die Verwalter der Arbeitsbereiche die Abrechnungsdaten selbst aendern
   * duerfen.
   *
   * **Abwesend heisst ja.** Das ist die Regel seit Issue 33: wer einen
   * Arbeitsbereich verwaltet, entscheidet auch fuer sein Konto. Der Schalter
   * ist die Ausnahme fuer Kunden, bei denen der Einkauf das nicht will — ein
   * Konzern, dessen Rechnungsanschrift keine Fachabteilung anfassen soll.
   *
   * Gesetzt wird er nur vom Superadmin: er beschraenkt den Kunden, also darf
   * der Kunde ihn nicht selbst umlegen.
   */ billingSelfService: z.boolean().optional(),
    /** Wann zuletzt abgeglichen — fürs Protokoll, nicht für die Entscheidung. */ at: z.iso.datetime().optional()
});
/**
 * The party we do business with — one per legal entity, and the single holder
 * of every external id. Tenants, licences and payments all hang off it.
 *
 * This level exists so no vendor is named anywhere else: replacing Stripe, Moco
 * or even HubSpot means swapping an id on one row per customer instead of
 * touching every call site that reads one off a tenant.
 *
 * Der Firmenname wird mitgehalten (`companyName`), so wie Anschrift, USt-IdNr.
 * und Rechnungsempfänger: seit LIC-26 führt das CRM sie alle, und der
 * Abgleich holt sie gemeinsam. Bis dahin stand hier, ein Zwischenspeicher
 * würde driften — das stimmt, gilt aber für die drei anderen genauso, und der
 * Preis dafür stand auf jeder Rechnung: der Anbieter bekam keinen Namen.
 *
 * Every external id is optional: an account exists as soon as somebody is a
 * customer, which is before it is linked to anything.
 */ /**
 * Was die Prüfung einer USt-IdNr. ergeben hat (Anforderung E1). Am Konto
 * archiviert, weil das Reverse-Charge-Verfahren beim Leistenden haftet: wer
 * ohne Steuer fakturiert, muss belegen können, dass die Nummer zum Zeitpunkt
 * gültig war — mit der Bestätigungsnummer der Abfrage.
 */ export const VatIdValidationSchema = z.object({
    /** Die geprüfte Nummer, normalisiert — eine geänderte Nummer macht die Prüfung hinfällig. */ vatId: z.string(),
    validatedAt: z.iso.datetime(),
    valid: z.boolean(),
    /**
   * `vies`: beim EU-System bestätigt. `syntax`: nur die Form geprüft — für
   * Nummern außerhalb der EU, die VIES nicht kennt und für die kein Reverse
   * Charge gilt.
   */ source: z.enum([
        'syntax',
        'vies'
    ]),
    /** VIES-Anfragekennung (qualifizierte Abfrage), sonst `null`. */ consultationNumber: z.string().nullable(),
    /** Name und Anschrift, wie das Register sie nennt — zum Abgleich, nicht als Wahrheit. */ name: z.string().nullable(),
    address: z.string().nullable()
});
/**
 * Eine App lässt eine USt-IdNr. prüfen (`ctx.accounts.checkVatId`): beim
 * Eintragen, nicht erst beim Rechnungslauf, und auch, wenn zur Firma noch
 * kein Konto besteht. Mit Firma wird das Ergebnis an ihr im CRM vermerkt.
 */ export const AppVatIdCheckRequestSchema = z.object({
    vatId: z.string().trim().min(1).max(40),
    hubspotCompanyId: z.string().regex(/^\d+$/).optional()
});
/**
 * Eine Person bei der Firma, aus dem CRM gelesen (LIC-26).
 *
 * Bliks kennt zwei Rollen, und beide sind Assoziations-Etiketten an der
 * HubSpot-Firma, keine Eigenschaften: den **Hauptansprechpartner** (die
 * Person, an die ein Angebot geht) und den **Abrechnungskontakt** (die
 * Person, die die Rechnung erwartet). Sie sind oft verschieden — der eine
 * entscheidet über den Kauf, der andere bucht ihn.
 *
 * Eine Kopie, kein Verweis: die Mail soll noch nachvollziehbar sein, wenn der
 * Kontakt im CRM längst jemand anderem gehört. Gepflegt wird er trotzdem nur
 * dort — Bliks liest ihn beim Verknüpfen und bei jedem Abgleich neu.
 */ export const CrmContactSchema = z.object({
    /** Der Kontakt im CRM — der Weg zurück, wenn jemand nachsehen will. */ hubspotContactId: z.string(),
    firstName: z.string().nullable(),
    lastName: z.string().nullable(),
    /** Ohne sie ist der Kontakt für einen Versand wertlos. */ email: z.email().nullable()
});
/**
 * Wohin die Rechnung wirklich geht.
 *
 * Zwei Quellen, eine Rangfolge: die Firmeneigenschaft `bliks_billing_email`
 * ist die ausdrueckliche Ansage („Rechnungen hierhin"), der als
 * Abrechnungskontakt gekennzeichnete Mensch ist die uebliche. Beide kommen
 * aus dem CRM (LIC-26); gepflegt ist meist nur eine.
 *
 * Hier und nicht je Aufrufer, weil zwei Stellen es unterschiedlich lesen
 * wuerden — und eine davon ist der Anbieter, der die Rechnung verschickt.
 * Genau das ist passiert: der Konfigurator meldete „kein Rechnungsempfaenger",
 * waehrend der Abrechnungskontakt danebenstand.
 */ export function invoiceRecipient(account) {
    return account.billingEmail?.trim() || account.billingContact?.email || null;
}
/** Wer die Rechnung erwartet, als Name — dieselbe Rangfolge. */ export function invoiceRecipientName(account) {
    if (account.billingContactName?.trim()) return account.billingContactName;
    const contact = account.billingContact;
    if (!contact) return null;
    const name = [
        contact.firstName,
        contact.lastName
    ].filter(Boolean).join(' ');
    return name || null;
}
/**
 * Die USt-IdNr., **sofern bestätigt** — sonst `null`.
 *
 * Nur eine bestätigte trägt das Reverse Charge, und wer ohne Steuer
 * fakturiert, haftet für die Nummer (E1). Die Prüfung muss zur aktuellen
 * Nummer gehören: wird die Nummer geändert, ist die alte Bestätigung hinfällig,
 * auch wenn sie noch am Konto hängt.
 *
 * Rein lesend, ohne Auffrischung — dafür gibt es `VatIdAssuranceService`. So
 * kann auch eine Liste über viele Konten danach fragen, ohne je Zeile ins Netz
 * zu gehen.
 */ export function confirmedVatIdOf(account) {
    const validation = account.vatIdValidation;
    if (!validation?.valid || !account.vatId) return null;
    return validation.vatId === account.vatId ? account.vatId : null;
}
export const AccountSchema = z.object({
    _id: z.string(),
    /**
   * Every foreign id this customer carries — the HubSpot company it *is*, the
   * customer record in whichever billing system collects from it. Data, not
   * columns: which system applies is read off the row (LIC-24 §2).
   */ externalRefs: z.array(ExternalRefSchema),
    /**
   * The account this one belongs to — a corporate group over its companies, a
   * chain over its branches, a firm over its partners (LIC-09).
   *
   * The relation grants NOTHING on its own: no reading across members, no
   * shared licence, no inherited payment method. It records who belongs to
   * whom, and every capability on top is decided separately and named
   * explicitly. A hierarchy quietly acquires meaning otherwise.
   */ parentAccountId: z.string().nullable(),
    billingEmail: z.email().nullable(),
    /**
   * Wie die Firma heißt — aus dem CRM gelesen, nicht hier gepflegt.
   *
   * Der Anbieter braucht ihn für die Rechnung; ohne ihn steht dort eine
   * Kundennummer und sonst nichts.
   */ companyName: z.string().nullable(),
    billingAddress: BillingAddressSchema.nullable(),
    /**
   * VAT identification number, which turns an intra-EU sale into a
   * reverse-charge one. Stripe collects it at checkout when the customer has
   * one; held here because a customer who pays by invoice never sees a Stripe
   * checkout, and reverse charge applies to them just the same.
   *
   * Not validated beyond its shape: the formats differ by member state and a
   * regex that is nearly right refuses real numbers. Validity is VIES's
   * question, and asking it is a slice of its own.
   */ vatId: z.string().trim().min(4).nullable(),
    /** Die letzte Prüfung von `vatId`; `null`, solange keine lief. */ vatIdValidation: VatIdValidationSchema.nullable(),
    /**
   * Woher die Stammdaten darüber stammen (LIC-26). `null` auf Konten, die
   * nie abgeglichen wurden — nicht dasselbe wie `crm`, und den Unterschied
   * braucht der Abrechnungsbereich, um „noch nie gelesen" von „steht so im
   * CRM" zu unterscheiden.
   */ masterDataOrigin: MasterDataOriginSchema.nullable(),
    /** Siehe {@link CrmShadowSchema}. Fehlt, solange nie abgeglichen wurde. */ crmShadow: CrmShadowSchema.optional(),
    /**
   * Siehe {@link CrmContactSchema}. `null`, solange die Firma im CRM keinen so
   * gekennzeichneten Kontakt hat — geraten wird keiner.
   */ primaryContact: CrmContactSchema.nullable(),
    /**
   * Der Abrechnungskontakt, siehe {@link CrmContactSchema}.
   *
   * Er sagt **wer**, nicht **wohin**: die Anschrift einer Rechnung bleibt
   * `billingEmail`, das Feld, das beide Seiten ändern dürfen (LIC-29).
   * Gehen die beiden auseinander, ist das sichtbar und nicht stillschweigend
   * aufgelöst — welche der beiden Angaben gilt, entscheidet ein Mensch.
   */ billingContact: CrmContactSchema.nullable(),
    /**
   * Wann Konto und CRM zuletzt auseinandergingen — gesetzt, wenn die Zahlseite
   * eine Adresse korrigiert hat, und geräumt, sobald der Abgleich sie wieder
   * einholt.
   */ masterDataDivergedAt: z.iso.datetime().nullable(),
    language: BillingLanguageSchema
});

//# sourceMappingURL=account.schema.js.map